import { lstatSync, realpathSync } from "node:fs";
import { dirname, isAbsolute, relative, resolve } from "node:path";

const invalid = () => new Error("AI_PROFILE_INVALID");
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const within = (child: string, parent: string) => {
  const rel = relative(parent, child);
  return rel === "" || (!rel.startsWith("../") && rel !== ".." && !isAbsolute(rel));
};
const hasControl = (path: string) =>
  [...path].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
const directory = (path: string) => {
  if (!isAbsolute(path) || hasControl(path)) throw invalid();
  const stat = lstatSync(path);
  if (!stat.isDirectory() || stat.isSymbolicLink()) throw invalid();
  return realpathSync(path);
};

/** Fixed pinned-native policy. No caller-supplied profile or sandbox override is admitted. */
export function prepareNativePolicy(input: {
  cwd: string;
  privateRoot: string;
  privateDirectory: string;
  sandboxMode: "read-only" | "workspace-write";
}) {
  const cwd = directory(input.cwd);
  const privateRoot = directory(input.privateRoot);
  const privateDirectory = directory(input.privateDirectory);
  if (
    within(cwd, privateRoot) ||
    within(privateRoot, cwd) ||
    !within(privateDirectory, privateRoot) ||
    privateDirectory === privateRoot ||
    (input.sandboxMode !== "read-only" && input.sandboxMode !== "workspace-write")
  )
    throw invalid();
  const stat = lstatSync(privateRoot);
  if (stat.mode & 0o077 || (process.getuid && stat.uid !== 0 && stat.uid !== process.getuid()))
    throw invalid();
  const filesystem = Object.freeze({
    [cwd]: input.sandboxMode === "workspace-write" ? "write" : "read",
    [privateRoot]: "deny",
  });
  const config = Object.freeze({
    default_permissions: "managed",
    features: Object.freeze({ use_legacy_landlock: false }),
    permissions: Object.freeze({
      managed: Object.freeze({
        extends: ":read-only",
        filesystem,
        network: Object.freeze({ enabled: false }),
      }),
    }),
  });
  // Native -c parses TOML, not JSON objects. Quote paths as TOML basic strings.
  const profile = `{ managed = { extends = ":read-only", filesystem = { ${Object.entries(filesystem)
    .map(([path, access]) => `${JSON.stringify(path)} = ${JSON.stringify(access)}`)
    .join(", ")} }, network = { enabled = false } } }`;
  const policy = Object.freeze({
    config,
    cliArgs: Object.freeze([
      "-c",
      'default_permissions="managed"',
      "-c",
      "features.use_legacy_landlock=false",
      "-c",
      `permissions=${profile}`,
    ]),
  });
  prepared.set(policy, { cwd, privateRoot, writable: input.sandboxMode === "workspace-write" });
  return policy;
}

type PreparedNativePolicy = ReturnType<typeof prepareNativePolicy>;
type Binding = { cwd: string; privateRoot: string; writable: boolean };
const prepared = new WeakMap<object, Binding>();
declare const admissionBrand: unique symbol;
export type NativeApprovalPolicy = { readonly [admissionBrand]: true };
const active = new WeakMap<object, Binding & { threadId: string; signal: AbortSignal }>();

/** Confirms composition and native selection, not live OS isolation (G1/G2 remain required). */
export function activateNativeApprovalPolicy(
  policy: PreparedNativePolicy,
  response: unknown,
  signal: AbortSignal,
): NativeApprovalPolicy {
  const binding = prepared.get(policy);
  if (
    !binding ||
    signal.aborted ||
    !record(response) ||
    !record(response.thread) ||
    typeof response.thread.id !== "string" ||
    !response.thread.id ||
    response.cwd !== binding.cwd ||
    !record(response.activePermissionProfile) ||
    response.activePermissionProfile.id !== "managed" ||
    response.activePermissionProfile.extends !== ":read-only"
  )
    throw invalid();
  const token = Object.freeze({}) as NativeApprovalPolicy;
  active.set(token, { ...binding, threadId: response.thread.id, signal });
  return token;
}

export function isNativeApprovalPolicyActive(
  policy: unknown,
  threadId: string,
): policy is NativeApprovalPolicy {
  if (!record(policy)) return false;
  const binding = active.get(policy);
  return Boolean(binding && !binding.signal.aborted && binding.threadId === threadId);
}

/** Resolve existing ancestors too, so new files cannot escape via a project symlink. */
export function allowsNativeApprovalPath(
  policy: NativeApprovalPolicy | undefined,
  path: string,
  access: "read" | "write",
): boolean {
  if (!policy) return false;
  const binding = active.get(policy);
  if (
    !binding ||
    binding.signal.aborted ||
    !isAbsolute(path) ||
    hasControl(path) ||
    (access !== "read" && access !== "write") ||
    (access === "write" && !binding.writable)
  )
    return false;
  try {
    let existing = resolve(path);
    const suffix: string[] = [];
    while (true) {
      try {
        lstatSync(existing);
        break;
      } catch (error) {
        if ((error as NodeJS.ErrnoException).code !== "ENOENT" || dirname(existing) === existing)
          return false;
        suffix.unshift(existing.slice(dirname(existing).length + 1));
        existing = dirname(existing);
      }
    }
    const canonical = resolve(realpathSync(existing), ...suffix);
    return within(canonical, binding.cwd) && !within(canonical, binding.privateRoot);
  } catch {
    return false;
  }
}
